import DOMPurify from "dompurify";

/**
 * Sanitize admin-authored HTML (Terms / Privacy / Refund policy content)
 * before passing to dangerouslySetInnerHTML.
 *
 * Replaces the legacy approach of `.replace(/style="[^"]*"/g, "")` which is
 * NOT a real sanitizer — it only stripped one attribute and left <script>,
 * event handlers (onerror, onclick), javascript: URLs, etc. untouched.
 *
 * Config is allowlist-based (safer default):
 *  - block <script>, <iframe>, <object>, <embed>, <style>, etc.
 *  - block inline event handlers (onclick, onerror, onload, ...)
 *  - allow only the tags/attrs commonly used in policy documents
 */
const POLICY_HTML_CONFIG = {
  ALLOWED_TAGS: [
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "br", "hr",
    "ul", "ol", "li",
    "strong", "em", "b", "i", "u", "s",
    "a",
    "blockquote",
    "table", "thead", "tbody", "tr", "th", "td",
    "span", "div",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "title"],
  ALLOW_DATA_ATTR: false,
  // Extra safety: external links open in new tab without leaking referrer
  ADD_ATTR: ["target"],
};

/**
 * Sanitize HTML for policy modals. Returns an empty string on non-string input
 * so the caller can use `dangerouslySetInnerHTML={{ __html: sanitizeHtml(x) }}`
 * without null-checking.
 */
export function sanitizePolicyHtml(dirtyHtml) {
  if (!dirtyHtml || typeof dirtyHtml !== "string") return "";
  return DOMPurify.sanitize(dirtyHtml, POLICY_HTML_CONFIG);
}

// Post-process: force target="_blank" + rel="noopener" on links.
// DOMPurify hook runs on every sanitize call.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});